import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "../../components/core/Dialog/Dialog";
import { AccountPinLocksPopup } from "../../components/common/AccountPinLocksPopup";
import { ChooseAvatarPopup } from "../../components/common/ChooseAvatarPopup";
import {
  IPinCodeLink,
  PinCodeDialog,
} from "../../components/common/PinCodeDialog";
import { ProfileFormPopup } from "../../components/common/ProfileFormPopup";
import { RemoveProfilePopup } from "../../components/common/RemoveProfilePopup";
import { PIN_LENGTH, VERIFICATION_CODE_LENGTH } from "../../models/profile";
import { displayPhone } from "../../models/user";
import { ProfileFlow } from "./useProfileFlow";

interface IProfileFlowDialogsProps {
  flow: ProfileFlow;
}

const ACCOUNT_PIN_INTRO_KEYS = {
  create: "ProfileFlow_Create Account PIN description",
  reset: "ProfileFlow_Reset Account PIN description",
  update: "ProfileFlow_Update Account PIN description",
} as const;

const ACCOUNT_PIN_TITLE_KEYS = {
  create: "ProfileFlow_Create Account PIN",
  reset: "ProfileFlow_Reset Account PIN",
  update: "ProfileFlow_Update Account PIN",
} as const;

export const ProfileFlowDialogs: FC<IProfileFlowDialogsProps> = ({ flow }) => {
  const { t } = useTranslation();
  const { dialog } = flow;

  const codeError = flow.errorKey ? t(flow.errorKey) : undefined;

  switch (dialog.type) {
    case "none":
      return null;

    case "profileForm":
      return (
        <ProfileFormPopup
          open
          mode={flow.form.mode}
          draft={flow.draft}
          nameError={
            flow.nameErrorKey
              ? t(flow.nameErrorKey)
              : flow.form.isNameInvalid
              ? t(
                  "ProfileForm_Profile name must be between 3-16 characters long.",
                )
              : undefined
          }
          canSubmit={flow.form.canSubmit}
          showKidMode={flow.form.showKidMode}
          profileLockControl={flow.form.profileLockControl}
          showRemove={flow.form.showRemove}
          focusField={dialog.focusField}
          onChangeName={flow.changeName}
          onToggleKidMode={flow.toggleKidMode}
          onToggleProfileLock={flow.toggleProfileLock}
          onManageProfileLock={flow.manageProfileLock}
          onOpenAvatar={flow.openAvatar}
          onRemove={flow.askToRemoveProfile}
          onSubmit={flow.submitForm}
          onClose={flow.close}
        />
      );

    case "chooseAvatar":
      return (
        <ChooseAvatarPopup
          open
          selectedId={flow.draft.inventoryId}
          onSave={flow.selectAvatar}
          onClose={flow.closeAvatar}
        />
      );

    case "enterPin": {
      const isProfileScope = dialog.scope === "profile";
      const { isResettingPin } = dialog;
      const canSwitch = flow.canSwitchPinScope(dialog.reason);
      // Account PIN is reset by OTP; a Profile PIN is reset with the Account PIN
      const links: IPinCodeLink[] = [
        {
          text: canSwitch
            ? t("ProfileFlow_Forgot PIN?")
            : t("ProfileFlow_Forgot my PIN"),
          gamepadId: "pin-code-forgot",
          onClick: () =>
            isProfileScope
              ? flow.forgotProfilePin(dialog.reason)
              : flow.forgotAccountPin(dialog.reason, dialog.scope),
        },
      ];
      if (canSwitch) {
        links.unshift({
          text: isProfileScope
            ? t("ProfileFlow_Use Account PIN")
            : t("ProfileFlow_Use Profile PIN"),
          gamepadId: "pin-code-switch-scope",
          onClick: () => flow.switchPinScope(dialog.scope, dialog.reason),
        });
      }

      return (
        <PinCodeDialog
          key={dialog.type}
          open
          title={
            isProfileScope
              ? t("ProfileFlow_Enter Profile PIN")
              : t("ProfileFlow_Enter Account PIN")
          }
          description={
            isResettingPin
              ? t("ProfileFlow_Reset Profile PIN description")
              : t("ProfileFlow_Enter your PIN code to access this profile.")
          }
          length={PIN_LENGTH}
          value={flow.code}
          errorMessage={codeError}
          links={isResettingPin ? [] : links}
          submitText={
            isResettingPin ? t("ProfileFlow_Submit") : t("ProfileFlow_Continue")
          }
          cancelText={
            isResettingPin ? t("ProfileFlow_Cancel") : t("ProfileFlow_Back")
          }
          isMasked
          onChange={flow.changeCode}
          onSubmit={() =>
            flow.submitPin(dialog.scope, dialog.reason, isResettingPin)
          }
          onCancel={() =>
            isResettingPin
              ? flow.switchPinScope("account", dialog.reason)
              : flow.goBackFrom(dialog.reason)
          }
        />
      );
    }

    case "createProfilePin": {
      const { resetFor } = dialog;
      return (
        <PinCodeDialog
          key={dialog.type}
          open
          title={
            resetFor
              ? t("ProfileFlow_Reset Profile PIN")
              : t("ProfileFlow_Create Profile PIN")
          }
          description={
            resetFor
              ? t(
                  "ProfileFlow_Your previous PIN code has been deleted. You can reset a new PIN code.",
                )
              : t("ProfileFlow_Set a 4-digit PIN code")
          }
          length={PIN_LENGTH}
          value={flow.code}
          errorMessage={codeError}
          submitText={t("ProfileFlow_Create")}
          cancelText={t("ProfileFlow_Cancel")}
          isMasked
          onChange={flow.changeCode}
          onSubmit={() =>
            resetFor ? flow.saveProfilePin(resetFor) : flow.createProfilePin()
          }
          onCancel={() =>
            resetFor
              ? flow.switchPinScope("account", resetFor)
              : flow.cancelProfilePin()
          }
        />
      );
    }

    case "accountPinIntro": {
      return (
        <Dialog
          open
          title={t(ACCOUNT_PIN_TITLE_KEYS[dialog.purpose.action])}
          description={t(ACCOUNT_PIN_INTRO_KEYS[dialog.purpose.action], {
            phone: displayPhone(flow.phone),
          })}
          mainAction={{
            text: t("ProfileFlow_Continue"),
            onClick: () => flow.sendVerificationCode(dialog.purpose),
            "data-gamepad": "default-selected",
          }}
          secondAction={{
            text: t("ProfileFlow_Cancel"),
            onClick: () => flow.cancelAccountPin(dialog.purpose),
            "data-gamepad": "account-pin-intro-cancel",
          }}
          errorMessage={codeError}
          onClose={() => flow.cancelAccountPin(dialog.purpose)}
        />
      );
    }

    case "verificationCode": {
      return (
        <PinCodeDialog
          key={dialog.type}
          open
          title={t(ACCOUNT_PIN_TITLE_KEYS[dialog.purpose.action])}
          description={t("ProfileFlow_Enter the 6-digit code sent to phone", {
            phone: displayPhone(flow.phone),
          })}
          length={VERIFICATION_CODE_LENGTH}
          value={flow.code}
          errorMessage={codeError}
          resend={
            flow.isOtpLockedOut
              ? undefined
              : {
                  prompt: t("ProfileFlow_Didn't receive the code?"),
                  actionText:
                    flow.resendIn > 0
                      ? t("ProfileFlow_Retry in seconds", {
                          seconds: flow.resendIn,
                        })
                      : t("ProfileFlow_Send again"),
                  onClick:
                    flow.resendIn > 0 ? undefined : flow.resendVerificationCode,
                }
          }
          submitText={t("ProfileFlow_Continue")}
          cancelText={t("ProfileFlow_Cancel")}
          onChange={flow.changeCode}
          onSubmit={() => flow.submitVerificationCode(dialog.purpose)}
          onCancel={() => flow.cancelAccountPin(dialog.purpose)}
        />
      );
    }

    case "setAccountPin": {
      return (
        <PinCodeDialog
          key={dialog.type}
          open
          title={t(ACCOUNT_PIN_TITLE_KEYS[dialog.purpose.action])}
          description={
            dialog.purpose.action === "reset"
              ? t("ProfileFlow_Old PIN is deleted, please enter your new PIN")
              : t("ProfileFlow_Set a 4-digit PIN code")
          }
          length={PIN_LENGTH}
          value={flow.code}
          submitText={
            dialog.purpose.action === "update"
              ? t("ProfileFlow_Update")
              : t("ProfileFlow_Create")
          }
          cancelText={t("ProfileFlow_Cancel")}
          isMasked
          onChange={flow.changeCode}
          onSubmit={() => flow.saveAccountPin(dialog.purpose)}
          onCancel={() => flow.cancelAccountPin(dialog.purpose)}
        />
      );
    }

    case "accountLocks":
      return (
        <AccountPinLocksPopup
          open
          profileLock={flow.accountLocks.profileLock}
          creationLock={flow.accountLocks.creationLock}
          focusUpdateButton={dialog.focusUpdateButton}
          onUpdateAccountPin={flow.updateAccountPin}
          onToggleProfileLock={flow.toggleAccountProfileLock}
          onToggleCreationLock={flow.toggleCreationLock}
          onSave={flow.saveAccountLocks}
          onClose={flow.cancelAccountLocks}
        />
      );

    case "removeProfile":
      return (
        <RemoveProfilePopup
          open
          onKeep={flow.keepProfile}
          onRemove={flow.confirmRemoveProfile}
        />
      );
  }
};
